import { Button } from '@design-system/Button';
import { useEffect, useRef, useState } from 'react';
import useStorifyConfig from '@hooks/useStorifyConfig';
import { EventTracker } from '@/utils';

interface StorifyMeProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLElement>,
    HTMLElement
  > {
  account: string;
  widget: string;
  story?: string;
  env: 'eu';
  segments?: string[];
  playbackOptions?: 'restartStoriesWhenOpen' | 'alwaysResumeStoryWhereStopped'; // default: 'alwaysResumeStoryWhereStopped'
  logLevel?: 'all' | 'info' | 'warning' | 'error' | 'none'; // default: 'none'
  init?: 'auto' | 'manual';
  reorderStories?: 'onClose' | undefined;
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'storifyme-collection': StorifyMeProps;
    }
  }
}

export const HealthResultCard = () => {
  const [showResults, setShowResults] = useState(false);
  const { tags, isStorifyEnabled, storifyWidgetId } = useStorifyConfig(true);
  const handleStartHere = () => {
    EventTracker.trackEvent('Home', 'home_hero_results_ready_start_here');
    setShowResults(true);
  };

  const storifymeRef = useRef(null);

  useEffect(() => {
    if (showResults && storifymeRef.current) {
      const storifymeElement = storifymeRef?.current;

      const handleReady = () => {
        // Set init to "manual"
        storifymeElement?.setAttribute('init', 'manual');

        storifymeElement?.openWidgetStoryByPosition(1);
      };

      storifymeElement.addEventListener('onLoad', handleReady);

      storifymeElement?.addEventListener('onStoryClosed', () => {
        setShowResults(false);
      });

      return () => {
        storifymeElement.removeEventListener('ready', handleReady);
      };
    }
    return;
  }, [showResults]);

  return (
    <>
      <div className="w-full h-60 p-8 rounded-xl bg-overlay-primary backdrop-blur-[50px] flex flex-col justify-between">
        <div className="space-y-2">
          <h2 className="typography-h1 text-white font-sans">
            Your health insights and recommendations are ready!
          </h2>
          <p className="typography-body text-white font-light">
            It's time to see your health in a whole new light.
          </p>
        </div>

        <Button
          variant="secondary"
          className="typography-caption1 w-24 h-9 bg-white text-primary"
          onClick={handleStartHere}
        >
          Start here
        </Button>
      </div>
      {showResults && isStorifyEnabled && (
        <div className="hidden">
          <storifyme-collection
            ref={storifymeRef}
            className="ml-6 hidden"
            account="viome-4593429"
            widget={storifyWidgetId}
            env="eu"
            playbackOptions="restartStoriesWhenOpen"
            segments={tags}
          />
        </div>
      )}
    </>
  );
};
